import React, { useEffect, useState } from "react";
import "./Authenticate.css";
import AuthBrandPanel from "./AuthBrandPanel";
import backArrow from "./../../../assets/img/auth-back-arrow.svg";
import { preventDefault } from "../../../utils/common";
import FormRenderer, { ComponentMapper, FormTemplate } from "../../common/FormRenderer";
import { LOGIN_FIELDS } from "./Authenticate.constants";
import { useVerifyOTP, useResendOTP } from "../../../backend/auth";
import { toast } from "react-toastify";
import PageLoader from "../../shared/PageLoader";
import { ERROR_MESSAGES } from "../../../utils/constants";

interface PostLoginOTPProps {
    onSuccess: (token: string) => void;
    onCancel: () => void;
    username: string;
    email: string;
    expiresIn?: number;
}

function PostLoginOTP({ onSuccess, onCancel, username, email, expiresIn }: PostLoginOTPProps) {
    const [form, setForm] = useState<any>({});
    const [secondsLeft, setSecondsLeft] = useState(expiresIn || 300);

    useEffect(() => {
        if (secondsLeft <= 0) {
            return;
        }
        const timer = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondsLeft]);

    const { verifyOTP, isLoading } = useVerifyOTP((response: any) => {
        if (response && response.data && response.data.token) {
            onSuccess(response.data.token);
        } else {
          response = response.response ? response.response : response
            const errorMessage = (response && response.data && response.data.error) || ERROR_MESSAGES.ERROR;
            toast.error(errorMessage);
        }
    }, (error: any) => {
        error = error.response ? error.response : error;
        const errorMessage = (error && error.data && error.data.error) || ERROR_MESSAGES.DEFAULT;
        toast.error(errorMessage);
    });

    const { resendOTP, isLoading: isResending } = useResendOTP((response: any) => {
        if (response && response.data) {
            toast.success("OTP resent successfully to your registered email");
            setSecondsLeft(expiresIn || 300);
        } else {
            response = response.response ? response.response : response
            const errorMessage = (response && response.data && response.data.error) || "Failed to resend OTP";
            toast.error(errorMessage);
        }
    }, (err: any) => {
        console.log(err)
        toast.error("Failed to resend OTP. Please try again.");
    });

    const schema = {
        fields: [{
            ...LOGIN_FIELDS.OTP,
            label: 'OTP sent to your registered email',
            placeholder: 'Enter OTP',
            className: 'ciel-auth-field',
        }]
    };

    function handleVerifyOTP({ otp }: any) {
        if (form.valid) {
            verifyOTP({ username, otp });
        }
    }

    function handleCancel(event: any) {
        preventDefault(event);
        onCancel();
    }

    function handleResendOTP(event: any) {
        preventDefault(event);
        resendOTP({username});
    }

    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;
    const countdownText = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;

    return (
        <>
            <div className="row m-0 ciel-auth">
                <div className="col-md-8 p-0 ciel-auth-brand-col">
                    <AuthBrandPanel />
                </div>

                <div className="col-md-4 p-0 ciel-auth-form-col">
                    <div className="ciel-auth-card">
                        <button type="button" className="ciel-auth-back-link" onClick={handleCancel}>
                            <img src={backArrow} alt="" />
                            Back to Login page
                        </button>
                        <div className="ciel-auth-heading">
                            <h2>Verify OTP</h2>
                        </div>

                        <FormRenderer
                            FormTemplate={FormTemplate}
                            initialValues={{ submitBtnText: 'Verify OTP', fullWidth: true }}
                            componentMapper={ComponentMapper}
                            schema={schema}
                            debug={setForm}
                            onSubmit={handleVerifyOTP}
                            clearOnUnmount={true}
                        />

                        <p className={`ciel-auth-countdown text-center ${secondsLeft <= 30 ? 'text-danger' : ''}`}>
                            OTP expires in {countdownText}
                        </p>

                        <a
                            href="/"
                            onClick={handleResendOTP}
                            className="ciel-auth-link text-center"
                            style={{ pointerEvents: isResending ? 'none' : 'auto', opacity: isResending ? 0.6 : 1 }}
                        >
                            {isResending ? 'Resending...' : 'Resend OTP'}
                        </a>
                    </div>
                </div>
            </div>
            {
                (isLoading || isResending) && (
                    <PageLoader>
                        {isLoading && <p>Verifying OTP...</p>}
                        {isResending && <p>Resending OTP...</p>}
                    </PageLoader>
                )
            }
        </>
    );
}

export default PostLoginOTP;
